import { normalizeScheme } from './inAppUrls';
// In-App tenant settings (D7 global cooldown, D13 test devices) — form helpers. Bounds mirror RRS
// service/inAppSettings.service.js; the server re-validates.
export const DEFAULT_COOLDOWN_SECONDS = 30;
export const MAX_COOLDOWN_SECONDS = 86400;
export const MAX_TEST_DEVICES = 50;

export function parseCooldown(input) {
  const s = String(input == null ? '' : input).trim();
  const n = /^\d+$/.test(s) ? Number(s) : NaN;
  if (!Number.isInteger(n) || n < 0 || n > MAX_COOLDOWN_SECONDS) {
    return { value: null, error: `Enter a whole number of seconds from 0 to ${MAX_COOLDOWN_SECONDS}.` };
  }
  return { value: n, error: null };
}

export function validateDeviceId(input, existing = []) {
  const v = String(input == null ? '' : input).trim();
  if (!v) return { value: null, error: 'Enter the device identifier.' };
  if (v.length > 200) return { value: null, error: 'Keep it under 200 characters.' };
  if ((existing || []).some((d) => d && d.device_id === v)) return { value: null, error: 'This device is already registered.' };
  if ((existing || []).length >= MAX_TEST_DEVICES) return { value: null, error: `At most ${MAX_TEST_DEVICES} test devices.` };
  return { value: v, error: null };
}

// B-14: tenant brand colour. RRS resolves var(--brand-primary) to it before sync and sends it as
// `brandColor`; empty = the default below. Stored upper-case #RRGGBB, or null.
export const DEFAULT_BRAND_COLOR = '#2563eb';
export function parseBrandColor(input) {
  const s = String(input == null ? '' : input).trim();
  if (!s) return { value: null, error: null };
  if (!/^#[0-9a-fA-F]{6}$/.test(s)) return { value: null, error: `Enter a colour as #RRGGBB, or leave it empty for the default (${DEFAULT_BRAND_COLOR}).` };
  return { value: s.toUpperCase(), error: null };
}

// P-8: deep-link schemes a message's deep_link may use (besides https). Empty = any well-formed
// custom scheme; javascript/data/vbscript/file/about/blob/http/content/intent never (./inAppUrls).
export const MAX_DEEP_LINK_SCHEMES = 20;
export function addDeepLinkScheme(current, input) {
  const list = current || [];
  const raw = String(input == null ? '' : input).trim();
  if (!raw) return { schemes: list, error: 'Enter a scheme name, e.g. myapp.' };
  const s = normalizeScheme(raw);
  if (!s) return { schemes: list, error: `"${raw.slice(0, 40)}" is not allowed: use a custom scheme name such as myapp (letters, digits, + - .; not http, https, javascript, data, file, intent …).` };
  if (list.includes(s)) return { schemes: list, error: 'Already in the list.' };
  if (list.length >= MAX_DEEP_LINK_SCHEMES) return { schemes: list, error: `At most ${MAX_DEEP_LINK_SCHEMES} schemes.` };
  return { schemes: [...list, s], error: null };
}

// DA-BUG-1132: tenant-wide display cap across all messages (per In-App session / per local day).
// 1–1000, or empty = off (null).
export const MAX_GLOBAL_CAP = 1000;
export function parseGlobalCap(input) {
  const s = String(input == null ? '' : input).trim();
  if (!s) return { value: null, error: null };
  const n = /^\d+$/.test(s) ? Number(s) : NaN;
  if (!Number.isInteger(n) || n < 1 || n > MAX_GLOBAL_CAP) return { value: null, error: `Enter a whole number from 1 to ${MAX_GLOBAL_CAP}, or leave it empty for no cap.` };
  return { value: n, error: null };
}

export function settingsFromApi(data) {
  const d = data && typeof data === 'object' ? data : {};
  return {
    cooldown: Number.isInteger(d.global_cooldown_seconds) ? d.global_cooldown_seconds : DEFAULT_COOLDOWN_SECONDS,
    devices: Array.isArray(d.test_devices) ? d.test_devices : [],
    // IAM-094; `domainsKnown` false when an older backend does not return the field.
    domains: Array.isArray(d.form_api_domains) ? d.form_api_domains : [],
    domainsKnown: Array.isArray(d.form_api_domains),
    // B-14; `brandKnown` false when an older backend does not return the key at all.
    brandColor: typeof d.brand_color === 'string' ? d.brand_color : '',
    brandKnown: Object.prototype.hasOwnProperty.call(d, 'brand_color'),
    // DA-BUG-1053: brand colour in dark appearance (sync brandColorDark); empty → the brand colour.
    brandDark: typeof d.brand_color_dark === 'string' ? d.brand_color_dark : '',
    brandDarkKnown: Object.prototype.hasOwnProperty.call(d, 'brand_color_dark'),
    capSession: Number.isInteger(d.global_max_per_session) ? String(d.global_max_per_session) : '',
    capDay: Number.isInteger(d.global_max_per_day) ? String(d.global_max_per_day) : '',
    capKnown: Object.prototype.hasOwnProperty.call(d, 'global_max_per_session') || Object.prototype.hasOwnProperty.call(d, 'global_max_per_day'),
    schemes: Array.isArray(d.deep_link_schemes) ? d.deep_link_schemes : [],
    schemesKnown: Array.isArray(d.deep_link_schemes),
  };
}

// Recent device ids (GET /in-app-settings/recent-devices → { devices:[{device_id,last_seen,registered}] },
// newest first) minus the ones already registered — the choices for the test-device picker.
export function recentDeviceOptions(recent, registered = []) {
  const taken = new Set((registered || []).map((d) => d && d.device_id));
  return (Array.isArray(recent) ? recent : [])
    .filter((d) => d && String(d.device_id || '').trim() && !d.registered && !taken.has(d.device_id));
}
